import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  type MeetingDetail,
  MeetingRefs,
  MeetingStateBadge,
  meetingDetailQueryOptions,
} from '@/entities/meeting'
import { useRequireCustomer } from '@/entities/session'
import { ApiError } from '@/shared/api'
import { formatDurationBetween, formatMeetingStart, formatTime, formatTimeNear } from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { ZoneNote } from '@/shared/ui/zone-note'
import { RecordingSection, TranscriptSection } from '@/widgets/meeting-artifacts'

export const Route = createFileRoute('/my/meetings_/$id')({
  loader: async ({ context, params }) => {
    try {
      return await context.queryClient.ensureQueryData(meetingDetailQueryOptions(params.id))
    } catch {
      return null
    }
  },
  head: ({ loaderData }) => ({
    meta: [{ title: `${loaderData?.title ?? 'Созвон'} · STIT Телемост` }],
  }),
  component: CustomerMeetingPage,
})

// Время встречи в шапке: плановое начало, фактический старт или прошедший
// интервал — как на карточке сотрудника, копия формулировок не нужна.
function meetingTimeInfo(meeting: MeetingDetail): string | undefined {
  const startedAt = meeting.startedAt ?? meeting.createdAt
  if (meeting.state === 'scheduled' && meeting.scheduledAt)
    return `начало ${formatMeetingStart(meeting.scheduledAt)}`
  if (meeting.state === 'active') return `идёт с ${formatMeetingStart(startedAt)}`
  if (meeting.state === 'ended' && meeting.endedAt) {
    const duration = formatDurationBetween(startedAt, meeting.endedAt)
    const planned = meeting.scheduledAt
      ? ` · планировался на ${formatTimeNear(meeting.scheduledAt, startedAt)}`
      : ''
    return `${formatMeetingStart(startedAt)} — ${formatTime(meeting.endedAt)}${duration ? ` · ${duration}` : ''}${planned}`
  }
  return undefined
}

// Карточка созвона глазами заказчика: что было, кто был, запись и расшифровка,
// вход в комнату. Управляющих действий (правка, приглашения, теги, удаление,
// гостевая ссылка, лобби) здесь нет — не по правам и не по задаче.
function CustomerMeetingPage() {
  useRequireCustomer()
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { data: meeting, isLoading, isError, error } = useQuery(meetingDetailQueryOptions(id))

  if (isLoading) {
    return (
      <div className="container-x px-6 py-10">
        <Spinner label="Загрузка созвона" />
      </div>
    )
  }

  if (isError || !meeting) {
    // 404 у заказчика двусмыслен намеренно: непомеченный созвон не отличается от
    // несуществующего — сам факт его наличия не раскрываем.
    const notFound = error instanceof ApiError && error.status === 404
    return (
      <div className="container-x px-6 py-10">
        <p className="text-accent">
          {notFound
            ? 'Созвон не найден. Возможно, его ещё не открыли вашей компании.'
            : 'Не удалось загрузить созвон. Проверьте соединение и повторите.'}
        </p>
      </div>
    )
  }

  const joinable = meeting.state === 'active' || meeting.state === 'scheduled'
  const timeInfo = meetingTimeInfo(meeting)

  return (
    <div className="container-x flex flex-col gap-6 px-4 py-8 sm:gap-8 sm:px-6 sm:py-10">
      <PageHeader
        title={meeting.title}
        // Заказчик приходит по ссылке и шапку с бейджем зоны может не увидеть
        // (на мобиле бейдж скрыт) — тег зоны стоит прямо за временем созвона.
        description={
          <>
            {`Организатор: ${meeting.organizer.name}`}
            {timeInfo && (
              <>
                {` · ${timeInfo} `}
                <ZoneNote />
              </>
            )}
          </>
        }
        status={<MeetingStateBadge state={meeting.state} />}
        actions={
          joinable ? (
            <Button size="sm" onClick={() => void navigate({ to: '/join/$id', params: { id } })}>
              Войти в звонок
            </Button>
          ) : undefined
        }
      />

      {/* Только проект: компания у заказчика всегда своя. */}
      <MeetingRefs meeting={meeting} hideCompany className="-mt-2" />

      {meeting.description && (
        <div className="-mt-2 flex flex-col gap-1">
          <p className="label">О встрече</p>
          <p className="whitespace-pre-line text-text-secondary">{meeting.description}</p>
        </div>
      )}

      {meeting.participants.length > 0 && (
        <section className="flex flex-col gap-4">
          <h2 className="t-h3">Участники · {meeting.participants.length}</h2>
          <ul className="flex flex-col gap-2">
            {meeting.participants.map((p) => (
              <li
                key={p.participantId}
                className="flex items-center gap-3 rounded-control bg-bg-surface px-4 py-3"
              >
                <Avatar name={p.name} />
                <p className="min-w-0 flex-1 truncate text-text-primary">{p.name}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {meeting.state === 'ended' && meeting.recording.exists && (
        <section className="flex flex-col gap-4">
          <h2 className="t-h3">Запись</h2>
          <RecordingSection
            meetingId={id}
            title={meeting.title}
            dateIso={meeting.scheduledAt ?? meeting.createdAt}
          />
        </section>
      )}

      {meeting.state === 'ended' && meeting.transcriptStatus !== 'none' && (
        <section className="flex flex-col gap-4">
          <h2 className="t-h3">Расшифровка</h2>
          <TranscriptSection
            meetingId={id}
            status={meeting.transcriptStatus}
            progress={meeting.transcriptProgress}
            title={meeting.title}
            dateIso={meeting.scheduledAt ?? meeting.createdAt}
          />
        </section>
      )}
    </div>
  )
}
